/**
 * Matrix cell colour bands, most negative to most positive.
 * Mirrors backend/src/Submission/Colour.php — order is load-bearing.
 */
export enum Colour {
  Red = "red",
  Orange = "orange",
  Yellow = "yellow",
  Green = "green",
  Cyan = "cyan",
  Blue = "blue",
  Violet = "violet",
}

/**
 * The ten phases in canonical order.
 * Mirrors backend/src/Questionnaire/Phase.php — the order the backend
 * serialises rowSums/colSums in and the axis order of `cells`.
 */
export const PHASES = [
  "pluto",
  "neptune",
  "uranus",
  "saturn",
  "sun",
  "moon",
  "mars",
  "mercury",
  "jupiter",
  "venus",
] as const

export type Phase = (typeof PHASES)[number]

export type Cell = {
  readonly value: number
  readonly colour: Colour
}

/**
 * Dispersion of the scores behind one aggregate, from
 * backend/src/Submission/PhaseStatistics.php.
 *
 * Population moments over the scaled 1…19 values. `excessKurtosis` is
 * null when every value is identical — m2 is 0 and the ratio undefined,
 * which an all-neutral submission legitimately produces.
 */
export type PhaseStatistics = {
  readonly n: number
  readonly mean: number
  readonly sd: number
  readonly excessKurtosis: number | null
}

/**
 * The 10×10 interference matrix as serialised by
 * backend/src/Submission/ResultMatrix.php.
 *
 * rowSums (topic-phase totals) and colSums (option-phase totals) are
 * keyed by phase strings (pluto…venus) in canonical order; cells align
 * positionally with that order on both axes. rowStats/colStats are keyed
 * the same way, and gridStats spans all 300 scores — the baseline a
 * single row or column is only meaningful against.
 */
export type ResultMatrix = {
  readonly rowSums: Record<Phase, number>
  readonly colSums: Record<Phase, number>
  readonly cells: Cell[][]
  readonly rowStats: Record<Phase, PhaseStatistics>
  readonly colStats: Record<Phase, PhaseStatistics>
  readonly gridStats: PhaseStatistics
}

/**
 * One topic's ten raw responses, from
 * backend/src/Submission/ResponseGrid.php.
 *
 * `values` are the raw −9…+9 answers in canonical option-phase order,
 * never the +10 scaled ones the matrix aggregates.
 */
export type ResponseRow = {
  readonly set: number
  readonly phase: Phase
  readonly topicSlug: string
  readonly values: number[]
}

/**
 * The 300 raw responses as 30 rows of ten: three sets of ten topics, one
 * per phase in each set, phases in canonical order.
 *
 * Rows n, n+10 and n+20 share a phase, so summing those three
 * element-wise gives the raw crosstab — the three direct responses
 * behind each (topic phase, option phase) pair, which is what the
 * interference grid stands in for. Operator-only.
 */
export type ResponseGrid = {
  readonly rows: ResponseRow[]
}
